import { lazy, Suspense } from 'react';
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import ProtectedRoute from "../components/feature/ProtectedRoute";
import AdminRoute from "../components/feature/AdminRoute";

// ── 懶載入所有頁面（分拆 bundle，提升首屏速度） ──
const Home             = lazy(() => import('../pages/home/page'));
const Login            = lazy(() => import('../pages/login/page'));
const Register         = lazy(() => import('../pages/register/page'));
const LineCallback     = lazy(() => import('../pages/auth-line-callback/page'));
const PrivacyPolicy    = lazy(() => import('../pages/privacy-policy/page'));
const TermsOfUse       = lazy(() => import('../pages/terms/page'));
const AiMatchPage      = lazy(() => import('../pages/ai-match/page'));
const AiFeaturesPage   = lazy(() => import('../pages/ai-features/page'));
const NoCommission     = lazy(() => import('../pages/no-commission/page'));
const Contact          = lazy(() => import('../pages/contact/page'));
const Regions          = lazy(() => import('../pages/regions/page'));
const CityPage         = lazy(() => import('../pages/regions/city-page'));
const PropertyDetail   = lazy(() => import('../pages/property-detail/page'));
const Publish          = lazy(() => import('../pages/publish/page'));
const MyProperties     = lazy(() => import('../pages/my-properties/page'));
const LandlordDashboard = lazy(() => import('../pages/landlord-dashboard/page'));
const AccountPage      = lazy(() => import('../pages/account/page'));
const AdminPage        = lazy(() => import('../pages/admin/page'));
const AdminThreadsPage = lazy(() => import('../pages/admin-threads/page'));
const NotFound         = lazy(() => import('../pages/NotFound'));

// 通用 Loading 畫面
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs text-gray-400">載入中...</p>
      </div>
    </div>
  );
}

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

const routes: RouteObject[] = [
  { path: "/",                  element: <S><Home /></S> },
  { path: "/login",             element: <S><Login /></S> },
  { path: "/register",          element: <S><Register /></S> },
  { path: "/auth/line/callback",element: <S><LineCallback /></S> },
  { path: "/safety-guide",      element: <Navigate to="/ai-features" replace /> },
  { path: "/privacy-policy",    element: <S><PrivacyPolicy /></S> },
  { path: "/terms",             element: <S><TermsOfUse /></S> },
  { path: "/ai-match",          element: <S><AiMatchPage /></S> },
  { path: "/ai-features",       element: <S><AiFeaturesPage /></S> },
  { path: "/no-commission",     element: <S><NoCommission /></S> },
  { path: "/contact",           element: <S><Contact /></S> },
  { path: "/regions",           element: <S><Regions /></S> },
  { path: "/regions/:city",     element: <S><CityPage /></S> },
  { path: "/property/:id",      element: <S><PropertyDetail /></S> },
  {
    path: "/admin",
    element: (
      <AdminRoute>
        <S><AdminPage /></S>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/threads",
    element: (
      <AdminRoute>
        <S><AdminThreadsPage /></S>
      </AdminRoute>
    ),
  },
  {
    path: "/publish",
    element: (
      <ProtectedRoute>
        <S><Publish /></S>
      </ProtectedRoute>
    ),
  },
  {
    path: "/my-properties",
    element: (
      <ProtectedRoute>
        <S><MyProperties /></S>
      </ProtectedRoute>
    ),
  },
  {
    path: "/landlord-dashboard",
    element: (
      <ProtectedRoute>
        <S><LandlordDashboard /></S>
      </ProtectedRoute>
    ),
  },
  {
    path: '/tenant-info',
    element: <Navigate to="/account" replace />,
  },
  {
    path: '/account',
    element: (
      <ProtectedRoute>
        <S><AccountPage /></S>
      </ProtectedRoute>
    ),
  },
  { path: "*", element: <S><NotFound /></S> },
];

export default routes;
